// write a function component named ReservationCancel. This compoment should render a div element with the text "Reservation Cancelled". The component should be exported as default.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
dayjs.locale('fr')

const getResto = async (restoName) => {
  const docs = await firebase.firestore()
    .collection('Restaurant')
    .where('urlName', '==', restoName)
    .get();

  if (!docs.empty) {
    const data = {
      id: docs.docs[0].id,
      ...docs.docs[0].data(),
    }

    return data;
  }
  else {
    return null;
  }
}

const ReservationCancel = () => {
  const { restoName, id } = useParams();
  const reservationId = id;

  const [resto, setResto] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false); // Added state for loading

  useEffect(() => {
    getResto(restoName.toLowerCase()).then(resto => {
      if (resto === null)
        setError('Le restaurant n\'existe pas');
      else {
        setResto(resto);
      }
    });
  }, [restoName]);

  useEffect(() => {
    if (resto !== null) {
      firebase.firestore()
        .collection('Restaurant')
        .doc(resto.id)
        .collection('Reservation')
        .doc(reservationId)
        .onSnapshot(doc => {
          if (doc.exists) {
            setReservation({
              id: doc.id,
              ...doc.data(),
            });
          }
          else {
            setReservation(null);
          }
        });
    }
  }, [resto?.id, reservationId, resto?.status]);

  useEffect(() => {
    if (resto !== null)
      document.title = 'Reservation ' + resto.reservationSettings.restoName;
  }, [resto]);

  const cancelReservation = () => {
    setLoading(true);

    setTimeout(async () => {
      await firebase.firestore()
        .collection('Restaurant')
        .doc(resto.id)
        .collection('Reservation')
        .doc(reservation.id)
        .update({ status: 5 });

      setLoading(false);
      console.log('Reservation cancelled successfully');
    }, 1500);
  }

  const getStatus = (status) => {
    if (status === 1)
      return 'En attente';
    else if (status === 2)
      return 'Confirmée';
    else if (status === 3)
      return 'Confirmée'; // automatiquement
    else if (status === 4)
      return 'Annulée';
    else if (status === 5)
      return 'Annulée'; // par le client
  }

  const getStatusBadge = (status) => {
    const statusText = getStatus(status);
    if (status === 1) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-50 text-yellow-700">
          {statusText}
        </span>
      );
    } else if (status === 2 || status === 3) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700">
          {statusText}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-700">
          {statusText}
        </span>
      );
    }
  };

  if (reservation !== null) {
    const date = new Date(reservation.reservationDate.seconds * 1000 + reservation.reservationDate.nanoseconds / 1000000);
    const readableDate = dayjs(date).format('DD MMMM YYYY à HH:mm');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Votre réservation</p>
            <h1 className="text-2xl font-bold text-gray-900">{resto.reservationSettings.restoName}</h1>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100"></div>

          {/* Reservation Details */}
          <div className="space-y-4">
            <div className="flex items-center justify-center space-x-2 text-gray-700">
              <span className="text-lg">📅</span>
              <span className="text-lg font-medium">{readableDate}</span>
            </div>

            <div className="flex items-center justify-center space-x-2 text-gray-600">
              <span className="text-lg">👤</span>
              <span>{reservation.firstname} {reservation.lastname}</span>
            </div>

            <div className="flex items-center justify-center space-x-2 text-gray-600">
              <span className="text-lg">👥</span>
              <span>{reservation.nbAdult} {reservation.nbAdult > 1 ? 'personnes' : 'personne'}</span>
            </div>
          </div>

          {/* Status */}
          <div className="flex justify-center">
            {getStatusBadge(reservation.status)}
          </div>

          {/* Cancel Button */}
          {(reservation.status === 2 || reservation.status === 3) && (
            <div className="pt-2">
              <button
                className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                onClick={cancelReservation}
                disabled={loading}
              >
                {loading && (
                  <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {loading ? 'Annulation en cours...' : 'Annuler votre réservation'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center space-y-4">
        <div className="text-4xl">❌</div>
        <h2 className="text-xl font-semibold text-gray-900">Réservation non trouvée</h2>
        <p className="text-gray-500">Cette réservation n'existe pas ou a été supprimée.</p>
      </div>
    </div>
  );

}

export default ReservationCancel;
